<template>
  <CRow>
    <CCol col="12" lg="12">
      <CAlert color="danger" closeButton :show.sync="dismissCountDown" fade>
        {{ err_msg }}
      </CAlert>
      <CCard>
        <CCardHeader>
          <strong>{{ $lang.instituteChain.crud.edit }}</strong>
        </CCardHeader>
        <CForm novalidate @submit.prevent="onSubmit">
          <CCardBody>
            <!-- ================= Basic Information ================= -->
            <CRow>
              <CCol md="12">
                <div class="form-group form-check">
                  <input type="checkbox" id="isRegistered" class="form-check-input"
                    v-model="instituteChain.isRegistered" />
                  <label class="form-check-label" for="isRegistered">Is Association Registered?</label>
                </div>
              </CCol>
              <!-- College (loaded from POST college/alumni) -->
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.collegeId.$error }">
                  <label class="form__label">College
                    <required_span />
                  </label>
                  <select class="form-control" v-model="instituteChain.collegeId" disabled>
                    <option value="" disabled>{{ collegeLoading ? "Loading..." : "Select College" }}</option>
                    <option v-for="c in collegeOptions" :key="c.value" :value="c.value">{{ c.label }}</option>
                  </select>
                  <small class="error" v-if="$v.instituteChain.collegeId.$error && !$v.instituteChain.collegeId.required">
                    College is required</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.bodyName.$error }">
                  <label class="form__label">Alumni Association Name
                    <required_span />
                  </label>
                  <input type="text" placeholder="Alumni Association Name" class="form-control"
                    v-model="instituteChain.bodyName" />
                  <small class="error" v-if="$v.instituteChain.bodyName.$error && !$v.instituteChain.bodyName.required">{{
        $lang.instituteChain.validation.required.bodyName }}</small>
                </div>
              </CCol>
              <CCol md="6" v-if="instituteChain.isRegistered">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.registeredNumber.$error }">
                  <label class="form__label">Registered Number
                    <required_span />
                  </label>
                  <input type="text" placeholder="Registered Number" class="form-control"
                    v-model="instituteChain.registeredNumber" />
                  <small class="error" v-if="$v.instituteChain.registeredNumber.$error && !$v.instituteChain.registeredNumber.required">
                    Registered number is required</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.description.$error }">
                  <label class="form__label">Description
                    <required_span />
                  </label>
                  <input type="text" placeholder="Description" class="form-control"
                    v-model="instituteChain.description" />
                  <small class="error" v-if="$v.instituteChain.description.$error && !$v.instituteChain.description.required">{{
        $lang.instituteChain.validation.required.description }}</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.mobileNumber.$error }">
                  <label class="form__label">Mobile Number
                    <required_span />
                  </label>
                  <vue-tel-input
                    v-model="instituteChain.mobileNumber"
                    v-on:country-changed="countryChanged"
                    v-bind="bindProps"
                    class="form-control"
                  ></vue-tel-input>
                  <small class="error" v-if="$v.instituteChain.mobileNumber.$error && !$v.instituteChain.mobileNumber.required">{{
        $lang.instituteChain.validation.required.mobileNumber }}</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.email.$error }">
                  <label class="form__label">Email
                    <required_span />
                  </label>
                  <input type="text" placeholder="Email" class="form-control"
                    v-model="instituteChain.email" />
                  <small class="error" v-if="$v.instituteChain.email.$error && !$v.instituteChain.email.required">{{
        $lang.instituteChain.validation.required.email }}</small>
                  <small class="error" v-if="$v.instituteChain.email.$error && !$v.instituteChain.email.email">{{
        $lang.instituteChain.validation.email.email }}</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group">
                  <label class="form__label">Address (optional)</label>
                  <textarea rows="2" placeholder="Address" class="form-control"
                    v-model="instituteChain.address"></textarea>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group">
                  <label class="form__label">Website (optional)</label>
                  <input type="text" placeholder="https://" class="form-control"
                    v-model="instituteChain.website" />
                </div>
              </CCol>
            </CRow>

            <!-- ================= Membership Plan ================= -->
            <CRow>
              <CCol md="12">
                <div class="form-group form-check">
                  <input type="checkbox" id="isFree" class="form-check-input"
                    v-model="instituteChain.isFree" />
                  <label class="form-check-label" for="isFree">Is Free?</label>
                </div>
              </CCol>

              <!-- Membership Price: clone set (Currency / Platform / SKU ID / Price) -->
              <CCol md="12" v-if="!instituteChain.isFree">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.membershipPrices.$error }">
                  <label class="form__label">Membership Price
                    <required_span />
                  </label>
                  <small class="d-block mb-2 text-muted">
                    One row per currency + platform. Android / iOS rows need the store product (SKU) ID.
                  </small>

                  <div class="table-responsive">
                    <table class="table table-bordered table-sm mb-2 price-table">
                      <thead class="thead-light">
                        <tr>
                          <th style="width: 20%">Currency</th>
                          <th style="width: 18%">Platform</th>
                          <th>SKU ID</th>
                          <th style="width: 18%">Price</th>
                          <th style="width: 1%" class="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, index) in instituteChain.membershipPrices" :key="row.uid">
                          <!-- Currency -->
                          <td>
                            <select class="form-control form-control-sm" v-model="row.currencyId">
                              <option value="" disabled>Select</option>
                              <option v-for="c in currencyOptions" :key="c.value" :value="c.value">{{ c.label }}</option>
                            </select>
                            <small class="error" v-if="$v.instituteChain.membershipPrices.$each[index].currencyId.$error">
                              Required</small>
                          </td>
                          <!-- Platform -->
                          <td>
                            <select class="form-control form-control-sm" v-model="row.platform">
                              <option v-for="p in platformOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
                            </select>
                            <small class="error" v-if="$v.instituteChain.membershipPrices.$each[index].platform.$error">
                              Required</small>
                          </td>
                          <!-- SKU ID -->
                          <td>
                            <input type="text" class="form-control form-control-sm" maxlength="150"
                              :placeholder="row.platform === 'web' ? 'Optional (e.g. gateway plan id)' : 'e.g. alumni_yearly_499'"
                              v-model.trim="row.skuId" />
                            <small class="error"
                              v-if="$v.instituteChain.membershipPrices.$each[index].skuId.$error && !$v.instituteChain.membershipPrices.$each[index].skuId.required">
                              SKU ID is required for {{ platformLabel(row.platform) }}</small>
                            <small class="error"
                              v-else-if="$v.instituteChain.membershipPrices.$each[index].skuId.$error && !$v.instituteChain.membershipPrices.$each[index].skuId.validSku">
                              Letters, numbers, "_" and "." only; must start with a letter or number</small>
                          </td>
                          <!-- Price -->
                          <td>
                            <input type="number" min="0" step="0.01" placeholder="0.00" class="form-control form-control-sm"
                              v-model="row.price" />
                            <small class="error"
                              v-if="$v.instituteChain.membershipPrices.$each[index].price.$error && !$v.instituteChain.membershipPrices.$each[index].price.required">
                              Required</small>
                            <small class="error"
                              v-else-if="$v.instituteChain.membershipPrices.$each[index].price.$error && !$v.instituteChain.membershipPrices.$each[index].price.validPrice">
                              Enter a valid price</small>
                          </td>
                          <!-- Actions -->
                          <td class="text-nowrap text-center">
                            <button type="button" class="btn btn-text btn-text-danger"
                              :disabled="instituteChain.membershipPrices.length === 1"
                              @click="removePrice(index)">
                              Delete
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <small class="error d-block"
                    v-if="$v.instituteChain.membershipPrices.$error && !$v.instituteChain.membershipPrices.minOne">
                    Add at least one price</small>
                  <small class="error d-block"
                    v-if="$v.instituteChain.membershipPrices.$error && !$v.instituteChain.membershipPrices.uniqueCombo">
                    Each currency + platform combination can be added only once</small>

                  <button type="button" class="btn btn-text btn-text-primary" @click="addPrice">
                    Add Price
                  </button>
                </div>
              </CCol>

              <!-- GST: only for paid memberships -->
              <template v-if="!instituteChain.isFree">
                <CCol md="12">
                  <div class="form-group form-check">
                    <input type="checkbox" id="isGstRegistered" class="form-check-input"
                      v-model="instituteChain.isGstRegistered" />
                    <label class="form-check-label" for="isGstRegistered">GST Registered?</label>
                  </div>
                </CCol>
                <template v-if="instituteChain.isGstRegistered">
                  <!-- GST Information -->
                  <CCol md="12">
                    <label class="form__label"><strong>GST Information</strong></label>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.gstNumber.$error }">
                      <label class="form__label">GST Number
                        <required_span />
                      </label>
                      <input type="text" placeholder="GST Number" class="form-control" maxlength="15"
                        v-model.trim="instituteChain.gstNumber"
                        @input="instituteChain.gstNumber = instituteChain.gstNumber.toUpperCase()" />
                      <small class="error" v-if="$v.instituteChain.gstNumber.$error && !$v.instituteChain.gstNumber.required">
                        GST number is required</small>
                      <small class="error" v-else-if="$v.instituteChain.gstNumber.$error && !$v.instituteChain.gstNumber.validGstin">
                        Enter a valid 15-character GST number (e.g. 24ABCDE1234F1Z5)</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.gstBusinessName.$error }">
                      <label class="form__label">Business Name
                        <required_span />
                      </label>
                      <input type="text" placeholder="Business Name" class="form-control"
                        v-model="instituteChain.gstBusinessName" />
                      <small class="error" v-if="$v.instituteChain.gstBusinessName.$error && !$v.instituteChain.gstBusinessName.required">
                        Business name is required</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.gstAddress.$error }">
                      <label class="form__label">Address
                        <required_span />
                      </label>
                      <input type="text" placeholder="Address" class="form-control"
                        v-model="instituteChain.gstAddress" />
                      <small class="error" v-if="$v.instituteChain.gstAddress.$error && !$v.instituteChain.gstAddress.required">
                        Address is required</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.gstState.$error }">
                      <label class="form__label">State
                        <required_span />
                      </label>
                      <select class="form-control" v-model="instituteChain.gstState">
                        <option value="" disabled>Select State</option>
                        <option v-for="s in StateOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
                      </select>
                      <small class="error" v-if="$v.instituteChain.gstState.$error && !$v.instituteChain.gstState.required">
                        State is required</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group">
                      <label class="form__label">Registration Date (optional)</label>
                      <input type="date" class="form-control" :max="today"
                        v-model="instituteChain.gstRegistrationDate" />
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.panNumber.$error }">
                      <label class="form__label">PAN Number (optional)</label>
                      <input type="text" placeholder="PAN Number" class="form-control" maxlength="10"
                        v-model.trim="instituteChain.panNumber"
                        @input="instituteChain.panNumber = instituteChain.panNumber.toUpperCase()" />
                      <small class="error" v-if="$v.instituteChain.panNumber.$error && !$v.instituteChain.panNumber.validPan">
                        Enter a valid PAN (e.g. ABCDE1234F)</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.gstTax.$error }">
                      <label class="form__label">GST Percentage (%)
                        <required_span />
                      </label>
                      <input type="number" min="0" max="100" placeholder="GST Percentage (%)" class="form-control"
                        v-model="instituteChain.gstTax" />
                      <small class="error" v-if="$v.instituteChain.gstTax.$error && !$v.instituteChain.gstTax.required">
                        GST percentage is required</small>
                      <small class="error" v-if="$v.instituteChain.gstTax.$error && !$v.instituteChain.gstTax.between">
                        GST percentage must be between 0 and 100</small>
                    </div>
                  </CCol>
                </template>

                <!-- Platform fee: separate setting, independent of GST, paid memberships only -->
                <CCol md="12">
                  <div class="form-group form-check">
                    <input type="checkbox" id="isPlatformFeeInclusive" class="form-check-input"
                      v-model="instituteChain.isPlatformFeeInclusive" />
                    <label class="form-check-label" for="isPlatformFeeInclusive">Platform Fee Inclusive?</label>
                  </div>
                </CCol>
              </template>
              <CCol md="12" v-if="!instituteChain.isFree">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.membershipDuration.$error }">
                  <label class="form__label">Membership Duration
                    <required_span />
                  </label>
                  <div>
                    <div class="form-check form-check-inline">
                      <input type="radio" id="durationYearly" class="form-check-input" value="Yearly"
                        v-model="instituteChain.membershipDuration" />
                      <label class="form-check-label" for="durationYearly">Yearly</label>
                    </div>
                    <div class="form-check form-check-inline">
                      <input type="radio" id="durationLifetime" class="form-check-input" value="LifeTime"
                        v-model="instituteChain.membershipDuration" />
                      <label class="form-check-label" for="durationLifetime">LifeTime</label>
                    </div>
                  </div>
                  <small class="error" v-if="$v.instituteChain.membershipDuration.$error && !$v.instituteChain.membershipDuration.required">
                    Membership duration is required</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.prefix.$error }">
                  <label class="form__label">Membership ID Prefix
                    <required_span />
                  </label>
                  <input type="text" placeholder="Membership ID Prefix" class="form-control"
                    v-model="instituteChain.prefix" />
                  <small class="error" v-if="$v.instituteChain.prefix.$error && !$v.instituteChain.prefix.required">
                    Membership ID prefix is required</small>
                </div>
              </CCol>
            </CRow>

            <!-- ================= Bank Information: paid memberships only ================= -->
            <CRow v-if="!instituteChain.isFree">
              <CCol md="12">
                <label class="form__label"><strong>Bank Information</strong></label>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.accountHolderName.$error }">
                  <label class="form__label">Account Holder Name
                    <required_span />
                  </label>
                  <input type="text" placeholder="Account Holder Name" class="form-control"
                    v-model="instituteChain.accountHolderName" />
                  <small class="error" v-if="$v.instituteChain.accountHolderName.$error && !$v.instituteChain.accountHolderName.required">
                    Account holder name is required</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.accountNumber.$error }">
                  <label class="form__label">Account Number
                    <required_span />
                  </label>
                  <input type="text" inputmode="numeric" placeholder="Account Number" class="form-control"
                    v-model.trim="instituteChain.accountNumber" />
                  <small class="error" v-if="$v.instituteChain.accountNumber.$error && !$v.instituteChain.accountNumber.required">
                    Account number is required</small>
                  <small class="error" v-else-if="$v.instituteChain.accountNumber.$error && !$v.instituteChain.accountNumber.validAccount">
                    Account number must be 9 to 18 digits</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.confirmAccountNumber.$error }">
                  <label class="form__label">Confirm Account Number
                    <required_span />
                  </label>
                  <input type="text" inputmode="numeric" placeholder="Confirm Account Number" class="form-control"
                    v-model.trim="instituteChain.confirmAccountNumber" @paste.prevent />
                  <small class="error" v-if="$v.instituteChain.confirmAccountNumber.$error && !$v.instituteChain.confirmAccountNumber.required">
                    Please confirm the account number</small>
                  <small class="error" v-else-if="$v.instituteChain.confirmAccountNumber.$error && !$v.instituteChain.confirmAccountNumber.sameAsAccount">
                    Account numbers do not match</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.ifscCode.$error }">
                  <label class="form__label">IFSC Code
                    <required_span />
                  </label>
                  <input type="text" placeholder="IFSC Code" class="form-control" maxlength="11"
                    v-model.trim="instituteChain.ifscCode"
                    @input="instituteChain.ifscCode = instituteChain.ifscCode.toUpperCase()" />
                  <small class="error" v-if="$v.instituteChain.ifscCode.$error && !$v.instituteChain.ifscCode.required">
                    IFSC code is required</small>
                  <small class="error" v-else-if="$v.instituteChain.ifscCode.$error && !$v.instituteChain.ifscCode.validIfsc">
                    Enter a valid IFSC code (e.g. SBIN0001234)</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.bankName.$error }">
                  <label class="form__label">Bank Name
                    <required_span />
                  </label>
                  <input type="text" placeholder="Bank Name" class="form-control"
                    v-model="instituteChain.bankName" />
                  <small class="error" v-if="$v.instituteChain.bankName.$error && !$v.instituteChain.bankName.required">
                    Bank name is required</small>
                </div>
              </CCol>
              <CCol md="6">
                <div class="form-group">
                  <label class="form__label">Address (optional)</label>
                  <input type="text" placeholder="Bank Address" class="form-control"
                    v-model="instituteChain.bankAddress" />
                </div>
              </CCol>
              <CCol md="12">
                <label class="form__label"><strong>UPI</strong></label>
              </CCol>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.upiId.$error }">
                  <label class="form__label">UPI ID (optional)</label>
                  <input type="text" placeholder="e.g. name@okaxis" class="form-control"
                    v-model.trim="instituteChain.upiId" />
                  <small class="error" v-if="$v.instituteChain.upiId.$error && !$v.instituteChain.upiId.validUpi">
                    Enter a valid UPI ID (e.g. name@okaxis)</small>
                </div>
              </CCol>
            </CRow>

            <!-- ================= Renewal Reminder (Days): Yearly only ================= -->
            <CRow v-if="isYearly">
              <CCol md="12">
                <div class="form-group">
                  <label class="form__label">Renewal Reminder (Days)</label>
                  <small class="d-block mb-2">Get notified this many days before renewal.</small>
                  <div class="mb-2" v-for="(reminder, index) in instituteChain.renewalReminders" :key="index">
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text">{{ index + 1 }}</span>
                      </div>
                      <input type="number" min="1" placeholder="Days" class="form-control"
                        v-model="reminder.days" />
                      <div class="input-group-append">
                        <span class="input-group-text">days before</span>
                      </div>
                      <div class="d-flex align-items-center ml-2">
                        <button type="button" class="btn btn-text btn-text-danger" @click="removeReminder(index)">
                          Delete
                        </button>
                      </div>
                    </div>
                    <small class="error"
                      v-if="$v.instituteChain.renewalReminders.$each[index].days.$error">
                      Enter a valid number of days</small>
                  </div>
                  <button type="button" class="btn btn-text btn-text-primary" @click="addReminder">
                    Add Reminder
                  </button>
                </div>
              </CCol>
            </CRow>

            <!-- ================= Donation ================= -->
            <!-- Own GST + bank details, saved separately from membership -->
            <CRow>
              <CCol md="12">
                <label class="form__label">
                  <strong>Donation</strong>
                  <span class="ml-1 text-muted" style="cursor: help"
                    title="Let members donate to the association. Donation GST and bank details are kept separate from membership.">
                    <CIcon name="cil-info" />
                  </span>
                </label>
              </CCol>
              <CCol md="12">
                <div class="form-group form-check">
                  <input type="checkbox" id="acceptDonations" class="form-check-input"
                    v-model="instituteChain.acceptDonations" />
                  <label class="form-check-label" for="acceptDonations">Accept Donations?</label>
                </div>
              </CCol>
              <template v-if="instituteChain.acceptDonations">
                <CCol md="12">
                  <div class="form-group form-check">
                    <input type="checkbox" id="isDonationGstRegistered" class="form-check-input"
                      v-model="instituteChain.isDonationGstRegistered" />
                    <label class="form-check-label" for="isDonationGstRegistered">GST Registered?</label>
                  </div>
                </CCol>

                <!-- Donation GST Information -->
                <template v-if="instituteChain.isDonationGstRegistered">
                  <CCol md="12">
                    <label class="form__label"><strong>Donation GST Information</strong></label>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationGstNumber.$error }">
                      <label class="form__label">GST Number
                        <required_span />
                      </label>
                      <input type="text" placeholder="GST Number" class="form-control" maxlength="15"
                        v-model.trim="instituteChain.donationGstNumber"
                        @input="instituteChain.donationGstNumber = instituteChain.donationGstNumber.toUpperCase()" />
                      <small class="error" v-if="$v.instituteChain.donationGstNumber.$error && !$v.instituteChain.donationGstNumber.required">
                        GST number is required</small>
                      <small class="error" v-else-if="$v.instituteChain.donationGstNumber.$error && !$v.instituteChain.donationGstNumber.validGstin">
                        Enter a valid 15-character GST number (e.g. 24ABCDE1234F1Z5)</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationGstBusinessName.$error }">
                      <label class="form__label">Business Name
                        <required_span />
                      </label>
                      <input type="text" placeholder="Business Name" class="form-control"
                        v-model="instituteChain.donationGstBusinessName" />
                      <small class="error" v-if="$v.instituteChain.donationGstBusinessName.$error && !$v.instituteChain.donationGstBusinessName.required">
                        Business name is required</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationGstAddress.$error }">
                      <label class="form__label">Address
                        <required_span />
                      </label>
                      <input type="text" placeholder="Address" class="form-control"
                        v-model="instituteChain.donationGstAddress" />
                      <small class="error" v-if="$v.instituteChain.donationGstAddress.$error && !$v.instituteChain.donationGstAddress.required">
                        Address is required</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationGstState.$error }">
                      <label class="form__label">State
                        <required_span />
                      </label>
                      <select class="form-control" v-model="instituteChain.donationGstState">
                        <option value="" disabled>Select State</option>
                        <option v-for="s in StateOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
                      </select>
                      <small class="error" v-if="$v.instituteChain.donationGstState.$error && !$v.instituteChain.donationGstState.required">
                        State is required</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group">
                      <label class="form__label">Registration Date (optional)</label>
                      <input type="date" class="form-control" :max="today"
                        v-model="instituteChain.donationGstRegistrationDate" />
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationPanNumber.$error }">
                      <label class="form__label">PAN Number (optional)</label>
                      <input type="text" placeholder="PAN Number" class="form-control" maxlength="10"
                        v-model.trim="instituteChain.donationPanNumber"
                        @input="instituteChain.donationPanNumber = instituteChain.donationPanNumber.toUpperCase()" />
                      <small class="error" v-if="$v.instituteChain.donationPanNumber.$error && !$v.instituteChain.donationPanNumber.validPan">
                        Enter a valid PAN (e.g. ABCDE1234F)</small>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationGstTax.$error }">
                      <label class="form__label">GST/Tax (%)
                        <required_span />
                      </label>
                      <input type="number" min="0" max="100" placeholder="GST/Tax (%)" class="form-control"
                        v-model="instituteChain.donationGstTax" />
                      <small class="error" v-if="$v.instituteChain.donationGstTax.$error && !$v.instituteChain.donationGstTax.required">
                        GST/Tax percentage is required</small>
                      <small class="error" v-if="$v.instituteChain.donationGstTax.$error && !$v.instituteChain.donationGstTax.between">
                        GST/Tax percentage must be between 0 and 100</small>
                    </div>
                  </CCol>
                </template>

                <!-- Donation Bank Information -->
                <CCol md="12">
                  <label class="form__label"><strong>Donation Bank Information</strong></label>
                </CCol>
                <CCol md="6">
                  <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationAccountHolderName.$error }">
                    <label class="form__label">Account Holder Name
                      <required_span />
                    </label>
                    <input type="text" placeholder="Account Holder Name" class="form-control"
                      v-model="instituteChain.donationAccountHolderName" />
                    <small class="error" v-if="$v.instituteChain.donationAccountHolderName.$error && !$v.instituteChain.donationAccountHolderName.required">
                      Account holder name is required</small>
                  </div>
                </CCol>
                <CCol md="6">
                  <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationAccountNumber.$error }">
                    <label class="form__label">Account Number
                      <required_span />
                    </label>
                    <input type="text" inputmode="numeric" placeholder="Account Number" class="form-control"
                      v-model.trim="instituteChain.donationAccountNumber" />
                    <small class="error" v-if="$v.instituteChain.donationAccountNumber.$error && !$v.instituteChain.donationAccountNumber.required">
                      Account number is required</small>
                    <small class="error" v-else-if="$v.instituteChain.donationAccountNumber.$error && !$v.instituteChain.donationAccountNumber.validAccount">
                      Account number must be 9 to 18 digits</small>
                  </div>
                </CCol>
                <CCol md="6">
                  <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationConfirmAccountNumber.$error }">
                    <label class="form__label">Confirm Account Number
                      <required_span />
                    </label>
                    <input type="text" inputmode="numeric" placeholder="Confirm Account Number" class="form-control"
                      v-model.trim="instituteChain.donationConfirmAccountNumber" @paste.prevent />
                    <small class="error" v-if="$v.instituteChain.donationConfirmAccountNumber.$error && !$v.instituteChain.donationConfirmAccountNumber.required">
                      Please confirm the account number</small>
                    <small class="error" v-else-if="$v.instituteChain.donationConfirmAccountNumber.$error && !$v.instituteChain.donationConfirmAccountNumber.sameAsAccount">
                      Account numbers do not match</small>
                  </div>
                </CCol>
                <CCol md="6">
                  <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationIfscCode.$error }">
                    <label class="form__label">IFSC Code
                      <required_span />
                    </label>
                    <input type="text" placeholder="IFSC Code" class="form-control" maxlength="11"
                      v-model.trim="instituteChain.donationIfscCode"
                      @input="instituteChain.donationIfscCode = instituteChain.donationIfscCode.toUpperCase()" />
                    <small class="error" v-if="$v.instituteChain.donationIfscCode.$error && !$v.instituteChain.donationIfscCode.required">
                      IFSC code is required</small>
                    <small class="error" v-else-if="$v.instituteChain.donationIfscCode.$error && !$v.instituteChain.donationIfscCode.validIfsc">
                      Enter a valid IFSC code (e.g. SBIN0001234)</small>
                  </div>
                </CCol>
                <CCol md="6">
                  <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationBankName.$error }">
                    <label class="form__label">Bank Name
                      <required_span />
                    </label>
                    <input type="text" placeholder="Bank Name" class="form-control"
                      v-model="instituteChain.donationBankName" />
                    <small class="error" v-if="$v.instituteChain.donationBankName.$error && !$v.instituteChain.donationBankName.required">
                      Bank name is required</small>
                  </div>
                </CCol>
                <CCol md="6">
                  <div class="form-group">
                    <label class="form__label">Address (optional)</label>
                    <input type="text" placeholder="Bank Address" class="form-control"
                      v-model="instituteChain.donationBankAddress" />
                  </div>
                </CCol>
                <CCol md="12">
                  <label class="form__label"><strong>UPI</strong></label>
                </CCol>
                <CCol md="6">
                  <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.donationUpiId.$error }">
                    <label class="form__label">UPI ID (optional)</label>
                    <input type="text" placeholder="e.g. name@okaxis" class="form-control"
                      v-model.trim="instituteChain.donationUpiId" />
                    <small class="error" v-if="$v.instituteChain.donationUpiId.$error && !$v.instituteChain.donationUpiId.validUpi">
                      Enter a valid UPI ID (e.g. name@okaxis)</small>
                  </div>
                </CCol>
              </template>
            </CRow>

            <!-- ================= Support Contact Details ================= -->
            <CRow>
              <CCol md="6">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.supportMobileNumber.$error }">
                  <label class="form__label">Support Mobile Number
                    <required_span />
                  </label>
                  <vue-tel-input
                    v-model="instituteChain.supportMobileNumber"
                    v-on:country-changed="supportCountryChanged"
                    v-bind="bindProps"
                    class="form-control"
                  ></vue-tel-input>
                  <small class="error" v-if="$v.instituteChain.supportMobileNumber.$error && !$v.instituteChain.supportMobileNumber.required">
                    Support mobile number is required</small>
                </div>
              </CCol>
            </CRow>

            <!-- ================= Rules ================= -->
            <CRow>
              <CCol md="12">
                <div class="form-group form-check">
                  <input type="checkbox" id="isRulesRequired" class="form-check-input"
                    v-model="instituteChain.isRulesRequired" />
                  <label class="form-check-label" for="isRulesRequired">Is Rules Required?</label>
                </div>
              </CCol>
              <CCol md="12" v-if="instituteChain.isRulesRequired">
                <div class="form-group" :class="{ 'form-group--error': $v.instituteChain.rules.$error }">
                  <label class="form__label">Rules
                    <required_span />
                  </label>
                  <textarea rows="4" placeholder="Rules" class="form-control"
                    v-model="instituteChain.rules"></textarea>
                  <small class="error" v-if="$v.instituteChain.rules.$error && !$v.instituteChain.rules.required">
                    Rules are required</small>
                </div>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="12">
                <div class="form-group" :class="{ 'form-group--error': '' }">
                  <img :src="SRCIMAGE" v-if="SRCIMAGE" alt="Profile Image"/>
                  <ImageCropper @profileImage="getProfile($event)"/>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
          <CCardFooter>
            <button type="submit" class="btn btn-text btn-text-primary mr-2" :disabled="submitted">
              {{ $lang.buttons.general.crud.submit }}
            </button>
            <button type="button" class="btn btn-text btn-text-danger" @click="cancel">
              {{ $lang.buttons.general.crud.cancel }}
            </button>
          </CCardFooter>
        </CForm>
      </CCard>
    </CCol>
  </CRow>
</template>
<script>
import Vue from "vue";
import VueForm from "vue-form";
import store from "/src/store/store.js";
import ImageCropper from "../../ImageCropper";
import { instituteChain, state } from "/src/store/url.js";
import { Mixin } from "/src/mixins/index.js";
import options from "/src/validations/validations.js";
import VueNotifications from "vue-notifications";
import { required, requiredIf, email, between } from "vuelidate/lib/validators";
import required_span from "../../../components/layouts/general/required-span";
import VueToasted from "vue-toasted";
import VueTelInput from "vue-tel-input-fix";
import "vue-tel-input-fix/dist/vue-tel-input.css";

function toast({ title, message, type, timeout, cb }) {
  if (type === VueNotifications.types.warn) type = "show";
  return Vue.toasted[type](message, {
    duration: timeout,
    position: "top-left",
  });
}

Vue.use(VueToasted);
Vue.use(VueTelInput);
const optionss = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast,
  position: "top-center",
};
Vue.use(VueNotifications, optionss);
Vue.use(VueForm, options);

// College list endpoint
const COLLEGE_ALUMNI_URL = "institute-chain/college";

// Membership price row factory (uid is only the v-for key, never sent)
let priceRowSeq = 0;
const newPriceRow = (overrides = {}) => ({
  id: null,            // membership_prices.id (null = new row)
  uid: ++priceRowSeq,
  currencyId: 1,       // INR
  platform: "android",
  skuId: "",
  price: "",
  ...overrides,
});

export default {
  name: "instituteChainEdit",
  mixins: [Mixin],
  components: {
    required_span,
    ImageCropper,
  },
  data() {
    return {
      SRCIMAGE: null,
      editId: this.$route.params.id,
      moduleState: state,
      bindProps: {
        mode: "international",
        placeholder: "Contact No.",
        defaultCountry: "IN",
        dropdownOptions: {
          showFlags: true,
        },
        validCharactersOnly: true,
      },
      // value = currency_id from your currencies table (TODO: confirm ids)
      currencyOptions: [{ value: 1, label: "Rs. (INR)" }],
      platformOptions: [
        { value: "android", label: "Android" },
        { value: "ios", label: "iOS" },
        { value: "web", label: "Web" },
      ],
      StateOptions: [],
      collegeOptions: [],
      collegeLoading: false,
      today: new Date().toISOString().slice(0, 10),
      submitted: false,
      err_msg: "",
      dismissCountDown: 0,
      module: instituteChain,
      instituteChain: {
        imageURL: "",
        imageRemove: 0,
        // Basic Information
        collegeId: "",
        isRegistered: false,
        bodyName: "",
        registeredNumber: "",
        description: "",
        mobileNumber: "",
        mobileNumberCode: "",
        isoCountryCode: "IN",
        email: "",
        address: "",
        website: "",
        // Membership Plan
        isFree: false,
        membershipPrices: [newPriceRow()],
        isGstRegistered: false,
        // GST Information
        gstNumber: "",
        gstBusinessName: "",
        gstAddress: "",
        gstState: "",
        gstRegistrationDate: "",
        panNumber: "",
        gstTax: "",
        isPlatformFeeInclusive: false,
        membershipDuration: "Yearly",
        prefix: "",
        // Bank Information (paid only)
        accountHolderName: "",
        accountNumber: "",
        confirmAccountNumber: "",
        ifscCode: "",
        bankName: "",
        bankAddress: "",
        upiId: "",
        // Renewal Reminder (Yearly only)
        renewalReminders: [{ days: "" }],
        // Donation
        acceptDonations: false,
        isDonationGstRegistered: false,
        // Donation GST
        donationGstNumber: "",
        donationGstBusinessName: "",
        donationGstAddress: "",
        donationGstState: "",
        donationGstRegistrationDate: "",
        donationPanNumber: "",
        donationGstTax: "",
        // Donation Bank
        donationAccountHolderName: "",
        donationAccountNumber: "",
        donationConfirmAccountNumber: "",
        donationIfscCode: "",
        donationBankName: "",
        donationBankAddress: "",
        donationUpiId: "",
        // Support Contact
        supportMobileNumber: "",
        supportMobileNumberCode: "",
        supportIsoCountryCode: "IN",
        // Rules
        isRulesRequired: false,
        rules: "",
      },
    };
  },
  validations: {
    instituteChain: {
      collegeId: { required },
      bodyName: { required },
      registeredNumber: {
        required: requiredIf((model) => model.isRegistered),
      },
      description: { required },
      mobileNumber: { required },
      email: { required, email },
      // Membership prices (plain functions so `this` is the component)
      membershipPrices: {
        minOne(rows) {
          return this.instituteChain.isFree || (rows && rows.length > 0);
        },
        uniqueCombo(rows) {
          if (this.instituteChain.isFree) return true;
          const keys = rows.map((r) => `${r.currencyId}|${r.platform}`);
          return new Set(keys).size === keys.length;
        },
        $each: {
          currencyId: {
            required: requiredIf(function () { return !this.instituteChain.isFree; }),
          },
          platform: {
            required: requiredIf(function () { return !this.instituteChain.isFree; }),
          },
          skuId: {
            // Store SKU needed for Android / iOS in-app purchase
            required: requiredIf(function (row) {
              return !this.instituteChain.isFree && row.platform !== "web";
            }),
            validSku(value) {
              return !value || /^[A-Za-z0-9][A-Za-z0-9._]{0,149}$/.test(value);
            },
          },
          price: {
            required: requiredIf(function () { return !this.instituteChain.isFree; }),
            validPrice(value) {
              if (value === "" || value === null) return true;
              return /^\d+(\.\d{1,2})?$/.test(String(value)) && Number(value) >= 0;
            },
          },
        },
      },
      gstNumber: {
        required: requiredIf((model) => !model.isFree && model.isGstRegistered),
        validGstin: (value, model) =>
          model.isFree || !model.isGstRegistered || !value ||
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value),
      },
      gstBusinessName: {
        required: requiredIf((model) => !model.isFree && model.isGstRegistered),
      },
      gstAddress: {
        required: requiredIf((model) => !model.isFree && model.isGstRegistered),
      },
      gstState: {
        required: requiredIf((model) => !model.isFree && model.isGstRegistered),
      },
      panNumber: {
        validPan: (value, model) =>
          model.isFree || !model.isGstRegistered || !value ||
          /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value),
      },
      gstTax: {
        required: requiredIf((model) => !model.isFree && model.isGstRegistered),
        between: between(0, 100),
      },
      membershipDuration: {
        required: requiredIf((model) => !model.isFree),
      },
      accountHolderName: {
        required: requiredIf((model) => !model.isFree),
      },
      accountNumber: {
        required: requiredIf((model) => !model.isFree),
        validAccount: (value, model) => model.isFree || !value || /^\d{9,18}$/.test(value),
      },
      confirmAccountNumber: {
        required: requiredIf((model) => !model.isFree),
        sameAsAccount: (value, model) => model.isFree || !value || value === model.accountNumber,
      },
      ifscCode: {
        required: requiredIf((model) => !model.isFree),
        validIfsc: (value, model) => model.isFree || !value || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value),
      },
      bankName: {
        required: requiredIf((model) => !model.isFree),
      },
      upiId: {
        validUpi: (value, model) =>
          model.isFree || !value || /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(value),
      },
      prefix: { required },
      renewalReminders: {
        $each: {
          days: {
            // Only validated when the reminder section is visible (Yearly)
            validDays: function (value) {
              if (!this.isYearly) return true;
              if (value === "" || value === null) return true;
              return Number.isInteger(Number(value)) && Number(value) >= 1;
            },
          },
        },
      },
      // Donation GST (accept donations + GST registered)
      donationGstNumber: {
        required: requiredIf((model) => model.acceptDonations && model.isDonationGstRegistered),
        validGstin: (value, model) =>
          !model.acceptDonations || !model.isDonationGstRegistered || !value ||
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value),
      },
      donationGstBusinessName: {
        required: requiredIf((model) => model.acceptDonations && model.isDonationGstRegistered),
      },
      donationGstAddress: {
        required: requiredIf((model) => model.acceptDonations && model.isDonationGstRegistered),
      },
      donationGstState: {
        required: requiredIf((model) => model.acceptDonations && model.isDonationGstRegistered),
      },
      donationPanNumber: {
        validPan: (value, model) =>
          !model.acceptDonations || !model.isDonationGstRegistered || !value ||
          /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value),
      },
      donationGstTax: {
        required: requiredIf((model) => model.acceptDonations && model.isDonationGstRegistered),
        between: between(0, 100),
      },
      // Donation Bank (accept donations)
      donationAccountHolderName: {
        required: requiredIf((model) => model.acceptDonations),
      },
      donationAccountNumber: {
        required: requiredIf((model) => model.acceptDonations),
        validAccount: (value, model) => !model.acceptDonations || !value || /^\d{9,18}$/.test(value),
      },
      donationConfirmAccountNumber: {
        required: requiredIf((model) => model.acceptDonations),
        sameAsAccount: (value, model) =>
          !model.acceptDonations || !value || value === model.donationAccountNumber,
      },
      donationIfscCode: {
        required: requiredIf((model) => model.acceptDonations),
        validIfsc: (value, model) => !model.acceptDonations || !value || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value),
      },
      donationBankName: {
        required: requiredIf((model) => model.acceptDonations),
      },
      donationUpiId: {
        validUpi: (value, model) =>
          !model.acceptDonations || !value || /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(value),
      },
      supportMobileNumber: { required },
      rules: {
        required: requiredIf((model) => model.isRulesRequired),
      },
    },
  },
  computed: {
    isYearly() {
      // Duration (and reminders) only apply to paid memberships
      return !this.instituteChain.isFree && this.instituteChain.membershipDuration === "Yearly";
    },
  },
  watch: {
    // Free membership: clear all pricing, bank, GST and platform-fee values
    "instituteChain.isFree"(isFree) {
      if (isFree) {
        const ic = this.instituteChain;
        ic.membershipPrices = [newPriceRow()];
        ic.accountHolderName = "";
        ic.accountNumber = "";
        ic.confirmAccountNumber = "";
        ic.ifscCode = "";
        ic.bankName = "";
        ic.bankAddress = "";
        ic.upiId = "";
        ic.isGstRegistered = false;
        ic.isPlatformFeeInclusive = false;
      }
    },
    // Turning GST off (directly or via Is Free) clears all GST fields
    "instituteChain.isGstRegistered"(registered) {
      if (!registered) {
        const ic = this.instituteChain;
        ic.gstNumber = "";
        ic.gstBusinessName = "";
        ic.gstAddress = "";
        ic.gstState = "";
        ic.gstRegistrationDate = "";
        ic.panNumber = "";
        ic.gstTax = "";
      }
    },
    // Donations off: clear donation bank fields and GST
    "instituteChain.acceptDonations"(accepts) {
      if (!accepts) {
        const ic = this.instituteChain;
        ic.isDonationGstRegistered = false;
        ic.donationAccountHolderName = "";
        ic.donationAccountNumber = "";
        ic.donationConfirmAccountNumber = "";
        ic.donationIfscCode = "";
        ic.donationBankName = "";
        ic.donationBankAddress = "";
        ic.donationUpiId = "";
      }
    },
    // Donation GST off (directly or via Accept Donations): clear all donation GST fields
    "instituteChain.isDonationGstRegistered"(registered) {
      if (!registered) {
        const ic = this.instituteChain;
        ic.donationGstNumber = "";
        ic.donationGstBusinessName = "";
        ic.donationGstAddress = "";
        ic.donationGstState = "";
        ic.donationGstRegistrationDate = "";
        ic.donationPanNumber = "";
        ic.donationGstTax = "";
      }
    },
  },
  mounted() {
    let self = this;
    self.dismissCountDown = 0;
    self.loadState();
    self.loadColleges();
    store.commit("showLoader", true); // Loader On
    axios
      .get(this.viewUrlApi(this.module, self.editId))
      .then((response) => {
        self.fillForm(response.data.data || {});
        store.commit("showLoader", false); // Loader Off
      })
      .catch(function () {
        store.commit("showLoader", false); // Loader Off
      });
  },
  methods: {
    // Fill the form from the detail API (keys already match the form fields)
    fillForm(details) {
      const ic = this.instituteChain;
      const str = (v) => (v === null || v === undefined ? "" : String(v));

      // Basic Information
      // Number, so it matches the dropdown option values (c.id)
      ic.collegeId = details.collegeId ? Number(details.collegeId) : "";
      ic.isRegistered = !!details.isRegistered;
      ic.bodyName = str(details.bodyName);
      ic.registeredNumber = str(details.registeredNumber);
      ic.description = str(details.description);
      ic.mobileNumberCode = str(details.mobileNumberCode) || "+91";
      ic.isoCountryCode = str(details.isoCountryCode) || "IN";
      // Prefix the dial code so vue-tel-input selects the right flag
      ic.mobileNumber = details.mobileNumber ? ic.mobileNumberCode + str(details.mobileNumber) : "";
      ic.email = str(details.email);
      ic.address = str(details.address);
      ic.website = str(details.website);
      this.SRCIMAGE = details.imageURL || null;

      // Membership Plan
      ic.isFree = !!details.isFree;
      // Price rows: Currency / Platform / SKU ID / Price (each gets a fresh uid)
      const prices = (details.membershipPrices || []).map((p) =>
        newPriceRow({
          id: p.id || null,
          currencyId: p.currencyId || 1,
          platform: p.platform || "android",
          skuId: str(p.skuId),
          price: str(p.price),
        })
      );
      // Older records with only the single legacy price: show it as one Web row
      if (!prices.length && !ic.isFree && details.price) {
        prices.push(newPriceRow({ currencyId: details.currencyId || 1, platform: "web", price: str(details.price) }));
      }
      ic.membershipPrices = prices.length ? prices : [newPriceRow()];
      ic.membershipDuration = details.membershipDuration || "Yearly";
      ic.prefix = str(details.prefix);
      ic.isPlatformFeeInclusive = !!details.isPlatformFeeInclusive;

      // GST Information
      ic.isGstRegistered = !!details.isGstRegistered;
      ic.gstNumber = str(details.gstNumber);
      ic.gstBusinessName = str(details.gstBusinessName);
      ic.gstAddress = str(details.gstAddress);
      ic.gstState = details.gstState || "";
      ic.gstRegistrationDate = str(details.gstRegistrationDate).slice(0, 10);
      ic.panNumber = str(details.panNumber);
      ic.gstTax = str(details.gstTax);

      // Bank Information
      ic.accountHolderName = str(details.accountHolderName);
      ic.accountNumber = str(details.accountNumber);
      ic.confirmAccountNumber = str(details.accountNumber);
      ic.ifscCode = str(details.ifscCode);
      ic.bankName = str(details.bankName);
      ic.bankAddress = str(details.bankAddress);
      ic.upiId = str(details.upiId);

      // Renewal Reminders
      const reminders = (details.renewalReminders || []).map((r) => ({ days: str(r.days) }));
      ic.renewalReminders = reminders.length ? reminders : [{ days: "" }];

      // Donation
      ic.acceptDonations = !!details.acceptDonations;
      ic.isDonationGstRegistered = !!details.isDonationGstRegistered;
      // Donation GST
      ic.donationGstNumber = str(details.donationGstNumber);
      ic.donationGstBusinessName = str(details.donationGstBusinessName);
      ic.donationGstAddress = str(details.donationGstAddress);
      ic.donationGstState = details.donationGstState || "";
      ic.donationGstRegistrationDate = str(details.donationGstRegistrationDate).slice(0, 10);
      ic.donationPanNumber = str(details.donationPanNumber);
      ic.donationGstTax = str(details.donationGstTax);
      // Donation Bank
      ic.donationAccountHolderName = str(details.donationAccountHolderName);
      ic.donationAccountNumber = str(details.donationAccountNumber);
      ic.donationConfirmAccountNumber = str(details.donationAccountNumber);
      ic.donationIfscCode = str(details.donationIfscCode);
      ic.donationBankName = str(details.donationBankName);
      ic.donationBankAddress = str(details.donationBankAddress);
      ic.donationUpiId = str(details.donationUpiId);

      // Support Contact
      ic.supportMobileNumberCode = str(details.supportMobileNumberCode) || "+91";
      ic.supportIsoCountryCode = str(details.supportIsoCountryCode) || "IN";
      ic.supportMobileNumber = details.supportMobileNumber
        ? ic.supportMobileNumberCode + str(details.supportMobileNumber)
        : "";

      // Rules
      ic.isRulesRequired = !!details.isRulesRequired;
      ic.rules = str(details.rules);
    },
    loadState() {
      let self = this;
      const postData = { countryId: 101 };
      axios
        .post(this.listUrlApi(this.moduleState), postData)
        .then((response) => {
          self.StateOptions = [];
          response.data.data.map(function (value, key) {
            self.StateOptions.push({ value: value.id, label: value.name });
          });
        });
    },
    // College dropdown: POST college/alumni -> [{ id, name }, ...]
    loadColleges() {
      let self = this;
      self.collegeLoading = true;
      axios
        .post(COLLEGE_ALUMNI_URL)
        .then((response) => {
          const list = (response.data && response.data.data) || [];
          self.collegeOptions = list.map((c) => ({ value: Number(c.id), label: c.name }));
        })
        .catch(() => {
          self.collegeOptions = [];
          self.err_msg = "Could not load colleges. Please refresh and try again.";
          self.dismissCountDown = 10;
        })
        .finally(() => {
          self.collegeLoading = false;
        });
    },
    getProfile(profile) {
      let self = this;
      self.instituteChain.imageURL = profile;
      if (profile === "") {
        self.instituteChain.imageRemove = 1;
      } else {
        self.instituteChain.imageRemove = 0;
      }
    },
    countryChanged(country) {
      let self = this;
      self.instituteChain.mobileNumberCode = "+" + country.dialCode;
      self.instituteChain.isoCountryCode = (country.iso2 || "IN").toUpperCase();
      let mobile = self.instituteChain.mobileNumber;
      self.instituteChain.mobileNumber = mobile
        .replace("+" + country.dialCode, "")
        .trim();
    },
    supportCountryChanged(country) {
      let self = this;
      self.instituteChain.supportMobileNumberCode = "+" + country.dialCode;
      self.instituteChain.supportIsoCountryCode = (country.iso2 || "IN").toUpperCase();
      let mobile = self.instituteChain.supportMobileNumber;
      self.instituteChain.supportMobileNumber = mobile
        .replace("+" + country.dialCode, "")
        .trim();
    },
    // ---------- Membership price clone set ----------
    platformLabel(value) {
      const p = this.platformOptions.find((o) => o.value === value);
      return p ? p.label : value;
    },
    nextFreePlatform(currencyId, fallback = "android") {
      const used = this.instituteChain.membershipPrices
        .filter((r) => r.currencyId === currencyId)
        .map((r) => r.platform);
      const free = this.platformOptions.find((p) => !used.includes(p.value));
      return free ? free.value : fallback;
    },
    addPrice() {
      this.instituteChain.membershipPrices.push(
        newPriceRow({ platform: this.nextFreePlatform(1) })
      );
    },
    removePrice(index) {
      if (this.instituteChain.membershipPrices.length > 1) {
        this.instituteChain.membershipPrices.splice(index, 1);
      }
    },
    // ---------- Renewal reminders ----------
    addReminder() {
      this.instituteChain.renewalReminders.push({ days: "" });
    },
    removeReminder(index) {
      this.instituteChain.renewalReminders.splice(index, 1);
    },
    sendAlert() {
      this.$root.$emit("alert", "test1", "test2");
    },
    onSubmit() {
      let self = this;
      this.$v.instituteChain.$touch();
      if (this.$v.instituteChain.$invalid) {
        self.submitted = false;
        this.submitStatus = "ERROR";
      } else {
        self.submitted = true;
        this.submitStatus = "PENDING";
        setTimeout(() => {
          let self = this;
          const ic = self.instituteChain;
          const flag = (v) => (v ? 1 : 0);
          const formData = new FormData();
          formData.append("image", ic.imageURL);
          formData.append("imageRemove", ic.imageRemove);
          // Basic Information
          formData.append("collegeId", ic.collegeId);
          formData.append("isRegistered", flag(ic.isRegistered));
          formData.append("bodyName", ic.bodyName);
          formData.append("registeredNumber", ic.isRegistered ? ic.registeredNumber : "");
          formData.append("description", ic.description);
          formData.append("mobileNumber", ic.mobileNumber);
          formData.append("mobileNumberCode", ic.mobileNumberCode);
          formData.append("isoCountryCode", ic.isoCountryCode);
          formData.append("email", ic.email);
          formData.append("address", ic.address);
          formData.append("website", ic.website);
          // Membership Plan
          const isPaid = !ic.isFree;
          const hasGst = isPaid && ic.isGstRegistered;
          formData.append("isFree", flag(ic.isFree));
          // Membership prices: Currency / Platform / SKU ID / Price rows
          formData.append(
            "membershipPrices",
            JSON.stringify(
              isPaid
                ? ic.membershipPrices.map((r) => ({
                    id: r.id,
                    currencyId: r.currencyId,
                    platform: r.platform,
                    skuId: r.skuId || null,
                    price: Number(r.price),
                  }))
                : []
            )
          );
          // Bank Information (paid only; confirmAccountNumber is UI-only)
          formData.append("accountHolderName", isPaid ? ic.accountHolderName : "");
          formData.append("accountNumber", isPaid ? ic.accountNumber : "");
          formData.append("ifscCode", isPaid ? ic.ifscCode : "");
          formData.append("bankName", isPaid ? ic.bankName : "");
          formData.append("bankAddress", isPaid ? ic.bankAddress : "");
          formData.append("upiId", isPaid ? ic.upiId : "");
          // GST (paid + GST registered only)
          formData.append("isGstRegistered", flag(hasGst));
          formData.append("gstNumber", hasGst ? ic.gstNumber : "");
          formData.append("gstBusinessName", hasGst ? ic.gstBusinessName : "");
          formData.append("gstAddress", hasGst ? ic.gstAddress : "");
          formData.append("gstState", hasGst ? ic.gstState : "");
          formData.append("gstRegistrationDate", hasGst ? ic.gstRegistrationDate : "");
          formData.append("panNumber", hasGst ? ic.panNumber : "");
          formData.append("gstTax", hasGst ? ic.gstTax : "");
          // Platform fee: separate from GST, driven by Free / Paid
          formData.append("isPlatformFeeInclusive", flag(isPaid && ic.isPlatformFeeInclusive));
          formData.append("membershipDuration", isPaid ? ic.membershipDuration : "");
          formData.append("prefix", ic.prefix);
          // Renewal Reminder (Yearly only)
          formData.append(
            "renewalReminders",
            JSON.stringify(
              self.isYearly
                ? ic.renewalReminders
                    .filter((r) => r.days !== "" && r.days !== null)
                    .map((r) => Number(r.days))
                : []
            )
          );
          // Donation (own GST + bank; confirm account number is UI-only)
          const acceptsDonation = !!ic.acceptDonations;
          const donationHasGst = acceptsDonation && ic.isDonationGstRegistered;
          formData.append("acceptDonations", flag(acceptsDonation));
          formData.append("isDonationGstRegistered", flag(donationHasGst));
          formData.append("donationGstNumber", donationHasGst ? ic.donationGstNumber : "");
          formData.append("donationGstBusinessName", donationHasGst ? ic.donationGstBusinessName : "");
          formData.append("donationGstAddress", donationHasGst ? ic.donationGstAddress : "");
          formData.append("donationGstState", donationHasGst ? ic.donationGstState : "");
          formData.append("donationGstRegistrationDate", donationHasGst ? ic.donationGstRegistrationDate : "");
          formData.append("donationPanNumber", donationHasGst ? ic.donationPanNumber : "");
          formData.append("donationGstTax", donationHasGst ? ic.donationGstTax : "");
          formData.append("donationAccountHolderName", acceptsDonation ? ic.donationAccountHolderName : "");
          formData.append("donationAccountNumber", acceptsDonation ? ic.donationAccountNumber : "");
          formData.append("donationIfscCode", acceptsDonation ? ic.donationIfscCode : "");
          formData.append("donationBankName", acceptsDonation ? ic.donationBankName : "");
          formData.append("donationBankAddress", acceptsDonation ? ic.donationBankAddress : "");
          formData.append("donationUpiId", acceptsDonation ? ic.donationUpiId : "");
          // Support Contact
          formData.append("supportMobileNumber", ic.supportMobileNumber);
          formData.append("supportMobileNumberCode", ic.supportMobileNumberCode);
          formData.append("supportIsoCountryCode", ic.supportIsoCountryCode);
          // Rules
          formData.append("isRulesRequired", flag(ic.isRulesRequired));
          formData.append("rules", ic.isRulesRequired ? ic.rules : "");

          axios.post(this.updateUrlweb(this.module, self.editId), formData)
            .then(function (response) {
              self.submitted = false;
              if (response.data.code === 200) {
                localStorage.setItem("showAlert", "This is my alert");
                self.sendAlert();
                self.$router.push({ path: "/institute-chain" });
              } else {
                self.err_msg = response.data.response.message;
                self.dismissCountDown = 10;
              }
            })
            .catch(function (error) {
              let data;
              if (error.response.data.error) {
                data = error.response.data.error.toString();
              } else {
                data = error.response.data.message;
              }
              self.err_msg = data;
              self.dismissCountDown = 10;
              self.submitted = false; //Enable Button
            });
        }, 500);
      }
    },
    cancel() {
      this.usersOpened
        ? this.$router.go(-1)
        : this.$router.push({ path: "/institute-chain" });
    },
  },
};
</script>
<style>
div .error {
  color: red;
}

.hasError label {
  color: red;
}

.price-table td {
  vertical-align: top;
}

/* Text-only buttons: no background until hover */
.btn-text {
  background: transparent;
  border: 1px solid transparent;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  border-radius: 0.25rem;
  box-shadow: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.btn-text:focus {
  box-shadow: none;
}
.btn-text-primary {
  color: #321fdb;
}
.btn-text-primary:hover:not(:disabled),
.btn-text-primary:focus-visible {
  background-color: #321fdb;
  color: #fff;
}
.btn-text-danger {
  color: #e55353;
}
.btn-text-danger:hover:not(:disabled),
.btn-text-danger:focus-visible {
  background-color: #e55353;
  color: #fff;
}
.btn-text:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>